import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { ThemeProvider } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { ACCOMMODATION_BOOKING } from 'graphql/queries/accommodations-bookings';
import { ACTIVITY_BOOKING } from 'graphql/queries/activities-bookings';
import {
  CANCEL_ACCOMMODATION_BOOKING,
  CANCEL_ACTIVITY_BOOKING
} from 'graphql/mutations/booking';

import { getReceivedBookedAccommodationData } from 'utils/book-accommodation/prepareReceivedBookedAccoomodationData';
import { getReceivedBookedActivityData } from 'utils/book-activity/prepareReceivedBookedActivityData';
import { getGeneralInfo } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getGeneralInfo as getGeneralActivityInfo } from 'utils/accommodations-and-activities/prepareReceivedActivityData';

import {
  CATEGORY_PRICES,
  FIXED_PRICES,
  DAILY_PRICES
} from 'pages/widget/constants';
import {
  GeneralCheckout,
  RelatedCheckout,
  Total
} from 'pages/widget/components/checkout/Checkout';

import { CircularLoader } from 'components';

import widgetTheme from 'widgetTheme';

import useStyles from './styles';

const BookingDetails = () => {
  const classes = useStyles();
  const { id, token } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const [booking, setBooking] = useState(null);
  const [product, setProduct] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [isAccommodationBooking, setIsAccommodationBooking] = useState(null);
  const [isCanceled, setCanceled] = useState(null);
  const [bookingCancelation, setBookingCancelation] = useState(
    'Booking cancelation'
  );

  const [cancellationPolicy, setCancellationPolicy] = useState('');
  const [cancellationNo, setCancellationNo] = useState('');
  const [cancelActivityBooking] = useMutation(CANCEL_ACTIVITY_BOOKING);
  const [cancelAccommodationBooking] = useMutation(
    CANCEL_ACCOMMODATION_BOOKING
  );

  const cancelAccommodation =
    location.pathname.indexOf('cancel-accommodation-booking') > 0;
  const cancelActivity =
    location.pathname.indexOf('cancel-activity-booking') > 0;
  const cancelation = cancelActivity || cancelAccommodation ? true : false;
  const cancelationAction = cancelation && !isCanceled ? true : false;

  const {
    loading: accommodationLoading,
    error: accommodationError,
    data: accommodationData
  } = useQuery(ACCOMMODATION_BOOKING, {
    skip:
      location.pathname.indexOf('accommodations-bookings') < 0 &&
      location.pathname.indexOf('cancel-accommodation-booking') < 0,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationBookingId: parseInt(id),
      token: token
    }
  });

  const {
    loading: activityLoading,
    error: activityError,
    data: activityData
  } = useQuery(ACTIVITY_BOOKING, {
    skip:
      location.pathname.indexOf('activities-bookings') < 0 &&
      location.pathname.indexOf('cancel-activity-booking') < 0,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityBookingId: parseInt(id),
      token: token
    }
  });

  const handleCancelationBooking = () => {
    if (id && token) {
      if (cancelAccommodation) {
        setBookingCancelation('Your booking was successfully cancelled');
        setCanceled(true);
        cancelAccommodationBooking({
          variables: {
            id: id,
            token: token
          }
        }).then(() => {});
        enqueueSnackbar('Booking was canceled', {
          variant: 'error'
        });
      } else if (cancelActivity) {
        setBookingCancelation('Your booking was successfully cancelled');
        setCanceled(true);
        cancelActivityBooking({
          variables: {
            ids: [id],
            token: token
          }
        }).then(() => {});
        enqueueSnackbar('Booking was canceled', {
          variant: 'error'
        });
      }
    }
  };

  useEffect(() => {
    if (!accommodationData) return;

    if (!accommodationData.accommodationsBookings.length) {
      enqueueSnackbar('Booking does not exist', {
        variant: 'error'
      });
      return;
    }

    const accommodationBooking = accommodationData.accommodationsBookings[0];

    if (accommodationBooking.isCanceled) {
      enqueueSnackbar('Booking was canceled', {
        variant: 'error'
      });
      return;
    }

    const accommodation = accommodationBooking.accommodation;
    const bookingData = getReceivedBookedAccommodationData(
      accommodationBooking
    );
    bookingData.activities = bookingData.activities.map(item => {
      item.isSelected = true;
      return item;
    });
    const productData = getGeneralInfo(accommodation);
    setIsAccommodationBooking(true);
    setBooking({
      values: bookingData
    });
    setCurrency(bookingData.currency);
    setProduct({
      ...productData,
      pricesType: getAccommodationPricesType(accommodation)
    });

    setCancellationPolicy(accommodationBooking.accommodation.cancellation || '');
    setCancellationNo(accommodationBooking.accommodation.cancellationNo || '');

  }, [accommodationData]);

  useEffect(() => {
    if (!activityData) return;

    if (!activityData.activitiesBookings.length) {
      enqueueSnackbar('Booking does not exist', {
        variant: 'error'
      });
      return;
    }

    const activityBooking = activityData.activitiesBookings[0];

    if (activityBooking.isCanceled) {
      enqueueSnackbar('Booking was canceled', {
        variant: 'error'
      });
      return;
    }

    const activity = activityBooking.activity;

    const bookingData = getReceivedBookedActivityData(activityBooking);
    bookingData.activities = bookingData.accommodations.map(item => {
      item.isSelected = true;
      return item;
    });
    const productData = getGeneralActivityInfo(activity);
    setIsAccommodationBooking(false);
    setBooking({
      values: bookingData
    });
    setCurrency(bookingData.currency);
    setProduct({
      ...productData,
      pricesType: activity['pricesType'],
      scheduleType: activity['scheduleType']
    });

    setCancellationPolicy(activityBooking.activity.cancellation || '');
    setCancellationNo(activityBooking.activity.cancellationNo || '');

  }, [activityData]);

  /*useEffect(() => {
    if (id && token) {
      if (cancelAccommodation) {
        cancelAccommodationBooking({
          variables: {
            id: 222,
            token: token
          }
        }).then(() => {});
      } else if (cancelActivity) {
        cancelActivityBooking({
          variables: {
            ids: [id],
            token: token
          }
        }).then(() => {});
      }
    }
  }, [cancelAccommodation, cancelActivity]);*/

  const getAccommodationPricesType = accommodation => {
    const firstPrice = accommodation.prices[0];

    if (firstPrice.participant.participant) {
      return CATEGORY_PRICES;
    } else if (firstPrice.day) {
      return DAILY_PRICES;
    } else {
      return FIXED_PRICES;
    }
  };

  if (accommodationLoading || activityLoading)
    return (
      <CircularLoader
        isLoading={true}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />
    );

  if (accommodationError || activityError || !booking) return null;

  return (
    <ThemeProvider theme={widgetTheme}>
      <div className={classes.wrapper}>
        <div className={classes.bookingDetails}>
          <Typography variant="h1" className={classes.title}>
            {!cancelationAction && (
              <CheckCircleIcon className={classes.doneIcon} />
            )}
            {cancelAccommodation || cancelActivity
              ? bookingCancelation
              : 'Your booking was successfully created'}
            {/*<span className={classes.bookingNumber}>ABS123</span>*/}
          </Typography>
          <Typography variant="h3" className={classes.bookingTitle}>
            Booking summary
          </Typography>
          <GeneralCheckout
            changeFormFieldValue={() => {}}
            widgetForm={booking}
            productData={product}
            currency={currency}
            isAccommodationBooking={isAccommodationBooking}
            isStatic={true}
          />
          <RelatedCheckout
            changeFormFieldValue={() => {}}
            widgetForm={booking}
            currency={currency}
            isAccommodationBooking={isAccommodationBooking}
            isStatic={true}
          />
          
          <Total
            widgetForm={booking}
            productData={product}
            currency={currency}
            isAccommodationBooking={isAccommodationBooking}
            isStatic={true}
          />
          <Typography variant="h3" className={classes.cancellationPolicy}>
            Cancellation Policy : 
          
            {cancellationNo ? (
                <>
                  <p>{cancellationNo}</p> 
                </>
              ) : (
                <>
                  <p>{cancellationPolicy || 'Not specified'}</p>
                </>
              )}
          </Typography>
          {cancelationAction && (
            <Button
              variant="outlined"
              color="primary"
              style={{ float: 'right' }}
              onClick={() => {
                handleCancelationBooking();
              }}
            >
              Confirm Cancellation
            </Button>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default BookingDetails;
